import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { submitContactMessage } from '@/api/contact';
import { useAuth } from '@/hooks/useAuth';
import { fetchDictItems, type DictItem } from '@/api/dict';
import { getPublicCms } from '@/api/cms';
import { usePublicSubPageContent } from '@/hooks/usePublicSubPageContent';

interface ContactContent { title: string; subtitle: string; email: string; github: string; wechatGroup: string; }
const EMPTY_CONTACT: ContactContent = {
  title: '',
  subtitle: '',
  email: '',
  github: '',
  wechatGroup: '',
};

function readText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function normalizeContact(raw: unknown): ContactContent {
  if (!raw || typeof raw !== 'object') return EMPTY_CONTACT;
  const parsed = raw as Record<string, unknown>;
  return {
    title: readText(parsed.title),
    subtitle: readText(parsed.subtitle),
    email: readText(parsed.email),
    github: readText(parsed.github),
    wechatGroup: readText(parsed.wechatGroup),
  };
}

export default function AboutPage() {
  const { content: subPageContent, error: subPageError } = usePublicSubPageContent();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [showAuthConfirm, setShowAuthConfirm] = useState(false);

  const aboutContent = subPageContent.about;
  const [contact, setContact] = useState<ContactContent>(EMPTY_CONTACT);
  const [contactError, setContactError] = useState<string | null>(null);

  const refreshContact = useCallback(async () => {
    try {
      const cms = await getPublicCms('home_page_content');
      setContact(normalizeContact(cms?.contact));
      setContactError(null);
    } catch (err) {
      setContact(EMPTY_CONTACT);
      setContactError(err instanceof Error ? err.message : '联系信息加载失败');
    }
  }, []);

  // 联系表单状态管理
  const [messageTypes, setMessageTypes] = useState<DictItem[]>([]);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', type: '', message: '' });
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);

  // 从系统字典加载消息类型列表
  useEffect(() => {
    fetchDictItems('contact_message_type')
      .then((res) => {
        if (res && res.items) {
          const enabledItems = res.items.filter((item) => item.isEnabled);
          setMessageTypes(enabledItems);
          const defaultItem = enabledItems.find((item) => item.isDefault) || enabledItems[0];
          if (defaultItem) {
            setFormData((p) => ({ ...p, type: defaultItem.value }));
          }
        }
      })
      .catch((err) => {
        console.error('Failed to fetch message types dictionary:', err);
        setMessageTypes([]);
        setFormData((p) => ({ ...p, type: '' }));
      });
  }, []);

  useEffect(() => {
    void refreshContact();
    const handleUpdate = () => void refreshContact();
    window.addEventListener('wanxiang_page_content_updated', handleUpdate);
    return () => window.removeEventListener('wanxiang_page_content_updated', handleUpdate);
  }, [refreshContact]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) return;
    if (formData.message.length > 500) return;
    if (!formData.type.trim()) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }
    
    // 检查用户是否已登录
    if (!isLoggedIn) {
      setShowAuthConfirm(true);
      return;
    }

    setFormStatus('submitting');
    try {
      await submitContactMessage({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || undefined,
        type: formData.type,
        message: formData.message
      });

      // 分发自定义事件以通知后台选项卡同步（如有打开）
      window.dispatchEvent(new CustomEvent('wanxiang_contact_message'));
      
      setFormStatus('success');
      const defaultType = messageTypes.find((item) => item.isDefault)?.value || messageTypes[0]?.value || '';
      setFormData({ name: '', email: '', phone: '', type: defaultType, message: '' });
      setTimeout(() => setFormStatus('idle'), 4000);
    } catch (err) {
      console.error('Failed to submit contact message:', err);
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <div className="bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white/70 text-sm mb-4">
            <i className="ri-information-line"></i>
            {aboutContent.header.badge || '无'}
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{aboutContent.header.title || '无'}</h1>
          <p className="text-white/60 max-w-xl mx-auto">
            {aboutContent.header.subtitle || '无'}
          </p>
          {subPageError && <p className="text-xs text-red-300 mt-3">页面内容加载失败：{subPageError}</p>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Story */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{aboutContent.story.title || '无'}</h2>
          <div className="space-y-4 text-gray-600 leading-relaxed">
            <p>{aboutContent.story.para1 || '无'}</p>
            <p>{aboutContent.story.para2 || '无'}</p>
            <p>{aboutContent.story.para3 || '无'}</p>
          </div>
        </div>

        {/* Values */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">{aboutContent.values.title || '无'}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: 'ri-open-source-line', title: aboutContent.values.item1Title || '无', desc: aboutContent.values.item1Desc || '无', color: '#0ea5e9' },
              { icon: 'ri-money-cny-circle-line', title: aboutContent.values.item2Title || '无', desc: aboutContent.values.item2Desc || '无', color: '#f97316' },
              { icon: 'ri-community-line', title: aboutContent.values.item3Title || '无', desc: aboutContent.values.item3Desc || '无', color: '#10b981' },
            ].map((v, idx) => (
              <div key={`${idx}-${v.icon}`} className="text-center p-6 bg-[#f8fafc] rounded-2xl border border-gray-100">
                <div className="w-14 h-14 flex items-center justify-center rounded-2xl mx-auto mb-4" style={{ backgroundColor: `${v.color}15` }}>
                  <i className={`${v.icon} text-2xl`} style={{ color: v.color }}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{v.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact Info */}
        <div id="contact" className="bg-gradient-to-br from-[#0f172a] to-[#1e3a5f] rounded-2xl p-8 mb-10">
          <h2 className="text-2xl font-bold text-white mb-3">{contact.title || '无'}</h2>
          <p className="text-white/60 mb-6">{contact.subtitle || '无'}</p>
          {contactError && <p className="text-xs text-red-300 mb-4">联系模块加载失败：{contactError}</p>}
          <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
            {contact.email && (
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <i className="ri-mail-line text-[#0ea5e9]"></i>
                {contact.email}
              </div>
            )}
            {contact.github && (
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <i className="ri-github-line text-[#0ea5e9]"></i>
                {contact.github}
              </div>
            )}
            {contact.wechatGroup && (
              <div className="flex items-center gap-2 text-white/70 text-sm">
                <i className="ri-wechat-line text-[#10b981]"></i>
                {contact.wechatGroup}
              </div>
            )}
          </div>
        </div>

        {/* Contact Form */}
        <div className="bg-[#f8fafc] rounded-2xl border border-gray-100 p-8">
          <div className="mb-6">
            <h3 className="text-xl font-bold text-gray-900 mb-1">发送消息</h3>
            <p className="text-gray-500 text-sm">填写以下表单，我们将尽快回复您的咨询或反馈</p>
          </div>

          {formStatus === 'success' ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="w-16 h-16 flex items-center justify-center bg-[#10b981]/10 rounded-full mb-4">
                <i className="ri-check-line text-[#10b981] text-3xl"></i>
              </div>
              <p className="font-semibold text-gray-900 text-lg mb-1">发送成功！</p>
              <p className="text-gray-500 text-sm">感谢您的留言，我们会尽快回复</p>
            </div>
          ) : (
            <form
              ref={formRef}
              id="contact-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                    您的称呼 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    placeholder="如：张先生"
                    value={formData.name}
                    onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                    required
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#0ea5e9] bg-white"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                    联系邮箱 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    required
                    className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#0ea5e9] bg-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                  联系电话 <span className="text-gray-400 font-normal">（选填）</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="如：138 0000 0000"
                  value={formData.phone}
                  onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                  className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#0ea5e9] bg-white"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">消息类型</label>
                <div className="flex gap-2 flex-wrap">
                  {messageTypes.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setFormData((p) => ({ ...p, type: t.value }))}
                      className={`px-4 py-2 text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-all ${
                        formData.type === t.value
                          ? 'bg-[#0ea5e9] text-white'
                          : 'bg-white border border-gray-200 text-gray-600 hover:border-[#0ea5e9]'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                  <input type="hidden" name="type" value={formData.type} />
                </div>
                {messageTypes.length === 0 && (
                  <p className="text-xs text-red-400 mt-2">消息类型加载失败，请稍后重试</p>
                )}
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-600 mb-1.5 block">
                  消息内容 <span className="text-red-400">*</span>
                </label>
                <textarea
                  name="message"
                  rows={5}
                  placeholder="请详细描述您的问题或建议，我们将认真阅读每一条反馈..."
                  value={formData.message}
                  onChange={(e) => setFormData((p) => ({ ...p, message: e.target.value }))}
                  maxLength={500}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:border-[#0ea5e9] resize-none bg-white"
                />
                <div className="flex justify-between mt-1">
                  <span className={`text-xs ${formData.message.length > 450 ? 'text-red-400' : 'text-gray-400'}`}>
                    {formData.message.length} / 500 字
                  </span>
                  {formData.message.length > 500 && (
                    <span className="text-xs text-red-400">超出字数限制，无法提交</span>
                  )}
                </div>
              </div>

              {formStatus === 'error' && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-red-500 text-sm">
                  <i className="ri-error-warning-line"></i>
                  提交失败，请稍后重试或直接发送邮件至 {contact.email}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'submitting' || formData.message.length > 500 || !formData.type}
                className="w-full py-3 bg-[#0ea5e9] text-white text-sm font-semibold rounded-xl hover:bg-[#0284c7] disabled:opacity-50 cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2"
              >
                {formStatus === 'submitting' ? (
                  <><i className="ri-loader-4-line animate-spin"></i>发送中...</>
                ) : (
                  <><i className="ri-send-plane-line"></i>发送消息</>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      {showAuthConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm transition-all duration-300 animate-fade-in" onClick={() => setShowAuthConfirm(false)}>
          <div 
            className="bg-white rounded-3xl p-8 w-full max-w-md mx-4 shadow-2xl border border-gray-100 transform scale-100 transition-all duration-300 animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-[#0ea5e9]/10 to-[#0284c7]/20 rounded-2xl mx-auto mb-5">
              <i className="ri-user-shared-line text-[#0ea5e9] text-3xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 text-center text-xl mb-2">您尚未登录</h3>
            <p className="text-sm text-gray-500 text-center mb-6 leading-relaxed">
              为了保障留言的真实性与您的服务体验，建议登录后再提交反馈。是否立即前往登录页面？
            </p>
            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowAuthConfirm(false);
                  navigate('/login?redirect=/about');
                }}
                className="w-full py-3 bg-[#0ea5e9] text-white text-sm font-semibold rounded-xl hover:bg-[#0284c7] cursor-pointer text-center whitespace-nowrap transition-all shadow-md shadow-[#0ea5e9]/20"
              >
                立即去登录
              </button>
              <button
                type="button"
                onClick={() => setShowAuthConfirm(false)}
                className="w-full py-3 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap text-center transition-all"
              >
                暂不登录 / 取消
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
